import { lazy as reactLazy } from "react";

const CHUNK_RELOAD_KEY = "stanza:chunk-reload-at";
const CHUNK_RELOAD_PARAM = "_stanza_chunk_retry";
const CHUNK_RELOAD_MEMORY_KEY = "__stanzaChunkReloadAt";
const CHUNK_RELOAD_COOLDOWN_MS = 30_000;

export function isDynamicImportError(error) {
  const message = error?.message || String(error || "");

  return /(?:failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|loading chunk .* failed|chunkloaderror)/i.test(
    message,
  );
}

function getSessionStorage() {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function getRetryParamReloadAt() {
  if (typeof window === "undefined") return 0;

  try {
    return Number(
      new URL(window.location.href).searchParams.get(CHUNK_RELOAD_PARAM),
    );
  } catch {
    return 0;
  }
}

function getLastReloadAt() {
  if (typeof window === "undefined") return 0;

  const storage = getSessionStorage();
  const retryParamReloadAt = getRetryParamReloadAt();

  try {
    return Number(
      storage?.getItem(CHUNK_RELOAD_KEY) ||
        window[CHUNK_RELOAD_MEMORY_KEY] ||
        retryParamReloadAt,
    );
  } catch {
    return Number(window[CHUNK_RELOAD_MEMORY_KEY] || retryParamReloadAt);
  }
}

function canReloadForChunkError() {
  const lastReloadAt = getLastReloadAt();
  return !lastReloadAt || Date.now() - lastReloadAt > CHUNK_RELOAD_COOLDOWN_MS;
}

function markChunkReload() {
  if (typeof window === "undefined") return;

  const reloadAt = String(Date.now());

  window[CHUNK_RELOAD_MEMORY_KEY] = reloadAt;

  const storage = getSessionStorage();
  if (!storage) return;

  try {
    storage.setItem(CHUNK_RELOAD_KEY, reloadAt);
  } catch {
    // A storage-quota/privacy failure should not prevent the normal reload.
  }
}

function reloadWithCacheBuster() {
  const url = new URL(window.location.href);
  url.searchParams.set(CHUNK_RELOAD_PARAM, String(Date.now()));
  window.location.replace(url.toString());
}

export function lazyWithRetry(loader, exportName) {
  return reactLazy(() =>
    loader()
      .then((module) => ({
        default: exportName ? module[exportName] : module.default,
      }))
      // A route chunk can fail after React has mounted; that is handled by
      // this retry path and Rollbar, not by the pre-React load marker.
      .catch((error) => {
        if (!isDynamicImportError(error) || !canReloadForChunkError()) {
          throw error;
        }

        markChunkReload();
        reloadWithCacheBuster();

        // Keep Suspense pending while the browser navigates. This prevents the
        // error boundary from rendering an error screen during the reload.
        return new Promise(() => {});
      }),
  );
}
